import type { CorRecurso, Recurso } from './tipos';

/** Só #rrggbb chega ao CSS (o banco já recusa outro formato; aqui é a segunda trava, porque vira valor de variável CSS). */
const COR_GOOGLE = /^#[0-9a-f]{6}$/i;

/**
 * A escolha em Profissionais e recursos, num tom fixo, e não pelo token: no Maré, o padrão do Principal, os tokens são quase todos
 * verdes (`--roxo` é #2F5D50, `--azul` é #00665B, `--ciano` é #005F66) e três pessoas saíam iguais a olho. `acento` e `neutro` não
 * entram: o primeiro é o padrão de todo recurso, o segundo não distingue ninguém. Tons da paleta de eventos do Google, menos o Ciano: os dela saíam quase iguais ao Azul ou ao Verde (rac-2 e rac-3).
 */
const DA_ESCOLHA: Partial<Record<CorRecurso, string>> = { azul: '#039be5', roxo: '#8e24aa', laranja: '#f4511e', verde: '#33b679', ciano: '#006064' };
/** Para quem não tem cor (ver `maisDistante`). Sem os tons pálidos, que quase somem na superfície clara (rac-1 VIS-M2). */
export const OPCOES = ['#039be5', '#f4511e', '#8e24aa', '#33b679', '#d50000', '#7986cb', '#616161', '#e67c73', '#0b8043', '#3f51b5', '#d81b60', '#795548', '#ad1457', '#00897b', '#ef6c00', '#006064'];
/** Do que nem está na lista de recursos e não tem cor nenhuma. */
const SEM_COR = 'var(--tinta-3)';

/** A cor que a escolha em Recursos dá, se der: `acento` e `neutro` deixam o sistema escolher. */
export const corDaEscolha = (c: CorRecurso) => DA_ESCOLHA[c] ?? null;

/** Cor CSS do que não está na lista de recursos (o recurso arquivado, por exemplo). */
export function corSoltaDoRecurso(corGoogle: string | null | undefined, cor: CorRecurso | null | undefined): string {
  if (corGoogle && COR_GOOGLE.test(corGoogle)) return corGoogle.toLowerCase();
  return (cor && DA_ESCOLHA[cor]) || SEM_COR;
}

const emRgb = (hex: string) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
const distancia = (a: string, b: string) => {
  const [x, y] = [emRgb(a), emRgb(b)];
  return Math.hypot(x[0]! - y[0]!, x[1]! - y[1]!, x[2]! - y[2]!);
};

/**
 * A opção livre mais longe das cores em uso: sem isso, a próxima da lista podia cair ao lado da cor do Google de outra pessoa
 * (#33b679 junto de #7bd148, dois verdes). Empate: a primeira da lista. Só com alguma livre.
 */
function maisDistante(usadas: Set<string>): string {
  const livres = OPCOES.filter((c) => !usadas.has(c));
  const emUso = [...usadas];
  let melhor = livres[0]!;
  let maior = -1;
  for (const c of livres) {
    const perto = emUso.length ? Math.min(...emUso.map((u) => distancia(c, u))) : 0;
    if (perto > maior) [melhor, maior] = [c, perto];
  }
  return melhor;
}

/**
 * Ficha 2026-11-03 (itens 2 e 4): a cor de cada recurso, como valor CSS.
 *  1. A do Google, quando lida. Duas pessoas com a mesma cor lá ficam iguais aqui: é a cópia pedida.
 *  2. Sem ela, a escolhida em Profissionais e recursos, se ninguém ainda a usa (a mais antiga fica com ela).
 *  3. Sem escolha (ou com a cor já usada), a livre mais longe das que estão em uso, nunca a mesma para todos. Em ordem de criação,
 *     para a cor não mudar quando alguém novo entra. Esgotadas as opções, recomeça.
 */
export function coresDaAgenda(recursos: Pick<Recurso, 'id' | 'cor' | 'cor_google' | 'created_at'>[]): Map<string, string> {
  const ordem = [...recursos].sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id));
  const saida = new Map<string, string>();
  const usadas = new Set<string>();
  const semCor: string[] = [];
  for (const r of ordem) {
    if (r.cor_google && COR_GOOGLE.test(r.cor_google)) {
      const hex = r.cor_google.toLowerCase();
      saida.set(r.id, hex);
      usadas.add(hex);
    }
  }
  for (const r of ordem) {
    if (saida.has(r.id)) continue;
    const propria = DA_ESCOLHA[r.cor];
    if (propria && !usadas.has(propria)) {
      saida.set(r.id, propria);
      usadas.add(propria);
    } else semCor.push(r.id);
  }
  // Esgotadas as opções, recomeça a paleta (rac-1 SPEC-B2: antes, todas as seguintes ficavam com a primeira).
  let excedente = 0;
  for (const id of semCor) {
    const livre = OPCOES.some((c) => !usadas.has(c));
    const cor = livre ? maisDistante(usadas) : OPCOES[excedente++ % OPCOES.length]!;
    saida.set(id, cor);
    usadas.add(cor);
  }
  return saida;
}
